import { useState, useEffect } from "react";
import { Menu, X, User, ShieldCheck, LogOut } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface HeaderProps {
  onOpenModal: (type: "surat" | "aspirasi" | "login" | "umkm") => void;
  adminName: string | null;
  onLogout: () => void;
}

export default function Header({ onOpenModal, adminName, onLogout }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Monitor active sections on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navMenuItems = [
    { label: "Beranda", path: "/", isAnchor: true, sectionId: "beranda" },
    { label: "Profil RW", path: "/#profil", isAnchor: true, sectionId: "profil" },
    { label: "Layanan", path: "/#layanan", isAnchor: true, sectionId: "layanan" },
    { label: "Informasi", path: "/#informasi", isAnchor: true, sectionId: "informasi" },
    { label: "UMKM", path: "/#umkm", isAnchor: true, sectionId: "umkm" },
    { label: "Galeri", path: "/galeri", isAnchor: false },
    { label: "Kontak", path: "/#kontak", isAnchor: true, sectionId: "kontak" }
  ];

  const handleNavClick = (path: string, isAnchor?: boolean, sectionId?: string) => {
    setIsMobileMenuOpen(false);
    
    if (isAnchor && sectionId) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: "smooth" });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }, 100);
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    } else {
      navigate(path);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-primary shadow-lg py-2.5 backdrop-blur-md" 
          : "bg-primary py-3.5 sm:py-4 border-b border-emerald-800/30"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* Logo Brand exactly as mockup */}
          <div 
            onClick={() => handleNavClick("/", true, "beranda")} 
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="relative flex items-center justify-center w-11 h-11 bg-white rounded-full border border-[#ffd22e] p-0.5 overflow-hidden transition-transform group-hover:scale-105">
              <img 
                src="/images/logo.jpeg" 
                alt="Logo Cibubur Villa 2" 
                className="w-full h-full object-cover rounded-full"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="flex flex-col select-none">
              <span className="text-white text-base font-extrabold tracking-widest leading-none">CIBUBUR</span>
              <span className="text-secondary text-xs font-bold tracking-tight leading-normal">VILLA 2 DIGITAL</span>
            </div>
          </div>

          {/* Center Links (Desktop only) */}
          <nav className="hidden lg:flex items-center gap-1.5" aria-label="Navigasi Utama">
            {navMenuItems.map((item) => {
              const active = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path.replace("/#", "/"));
              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.path, item.isAnchor, item.sectionId)}
                  className={`relative px-4 py-2 text-sm font-semibold tracking-wide rounded-lg transition-all cursor-pointer ${
                    active 
                      ? "text-secondary font-bold" 
                      : "text-gray-100 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute bottom-1.5 left-4 right-4 h-0.5 bg-secondary rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right Action buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {adminName && (
              <div className="flex items-center gap-2">
                <div className="bg-primary-light border border-secondary/20 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-white text-xs">
                  <ShieldCheck className="w-4 h-4 text-secondary" />
                  <span className="font-semibold">HI, ADMIN FITRI</span>
                </div>
                <button 
                  onClick={onLogout}
                  className="p-2 bg-red-600/20 hover:bg-red-600/40 text-red-100 rounded-lg transition-all cursor-pointer"
                  title="Logout Admin"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Hamburger (Mobile only) */}
          <div className="flex lg:hidden items-center gap-2">
            {adminName && (
              <div className="bg-white/10 p-2.5 rounded text-white mr-1" title="Admin Active">
                <ShieldCheck className="w-5 h-5 text-secondary" />
              </div>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-3 text-white hover:bg-white/5 rounded-lg transition-all focus:outline-none cursor-pointer"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </div>

      {/* Slide-out Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-primary border-t border-white/5 shadow-2xl py-4 px-6 space-y-3 transition-all">
          <div className="flex flex-col space-y-1">
            {navMenuItems.map((item) => {
              const active = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path.replace("/#", "/"));
              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.path, item.isAnchor, item.sectionId)}
                  className={`w-full text-left py-2.5 px-4 rounded-lg font-semibold text-sm transition-all cursor-pointer ${
                    active 
                      ? "bg-white/10 text-secondary border-l-4 border-secondary pl-3" 
                      : "text-gray-100 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          
          <div className="pt-3 border-t border-white/10">
            {adminName && (
              <div className="flex justify-between items-center bg-white/5 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-white text-xs">
                  <ShieldCheck className="w-4 h-4 text-secondary" />
                  <span>Admin: <strong>{adminName}</strong></span>
                </div>
                <button 
                  onClick={() => {
                    onLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-[10px] uppercase font-bold rounded"
                >
                  LOGOUT
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
